import { zodResolver } from '@hookform/resolvers/zod';
import { debounce, isEqual } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import {
  QBox,
  QButton,
  QReorderableCard,
  QReorderableCardStack,
} from '@qualio/ui-components';

import { QualioDocument } from '../../../api/model/document';

import { useDeleteTrainingAssessment } from './hooks/useDeleteTrainingAssessment';
import { useSaveTrainingAssessment } from './hooks/useSaveTrainingAssessment';

import { EditorStatus, EditorStatusContext } from '../DocumentOverview/Context';
import { GenerateTrainingAssessmentAI } from './GenerateTrainingAssessmentAI';
import { TrainingAssessmentEmptyState } from './TrainingAssessmentEmptyState';
import { TrainingAssessmentFormQuestion } from './TrainingAssessmentFormQuestion';

import {
  TrainingAssessment,
  TrainingAssessmentAIResponse,
  TrainingAssessmentForm,
} from './types/trainingAssessment';

type TrainingAssessmentProps = {
  documentId: QualioDocument['id'];
  trainingAssessment?: TrainingAssessment;
};

export const EditTrainingAssessment: React.FC<TrainingAssessmentProps> = ({
  documentId,
  trainingAssessment,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { setTrainingAssessmentStatus } = useContext(EditorStatusContext);

  const { mutateAsync: saveTrainingAssessment } = useSaveTrainingAssessment(
    documentId,
    setTrainingAssessmentStatus,
  );

  const { mutateAsync: deleteTrainingAssessment } = useDeleteTrainingAssessment(
    documentId,
    setTrainingAssessmentStatus,
  );

  const methods = useForm<TrainingAssessmentForm>({
    defaultValues: {
      questions: (trainingAssessment?.questions ?? []).map((q) => ({
        ...q,
        stableKey: crypto.randomUUID(),
        answers: q.answers.map((a) => ({
          ...a,
          stableKey: crypto.randomUUID(),
        })),
      })),
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(TrainingAssessmentForm),
  });

  const { append, fields, swap, remove } =
    useFieldArray<TrainingAssessmentForm>({
      control: methods.control,
      name: 'questions',
    });

  const handleSubmit = useCallback(
    async (data: TrainingAssessmentForm) => {
      const currentTrainingAssessment: TrainingAssessment | undefined =
        queryClient.getQueryData(['trainingAssessment', documentId]);
      const questions = data.questions.map((question) => {
        const answers = question.answers.map(
          ({ stableKey, ...answer }) => answer,
        );
        if (question.id !== undefined) {
          const { stableKey, ...rest } = question;
          return { ...rest, answers };
        }
        const { id, stableKey, ...questionDTO } = question;
        return { ...questionDTO, answers };
      });

      if (questions.length === 0) {
        await deleteTrainingAssessment();
      } else {
        const payload = {
          ...(currentTrainingAssessment && {
            id: currentTrainingAssessment.id,
            description: currentTrainingAssessment.description,
          }),
          document_id: documentId,
          questions,
        };
        await saveTrainingAssessment(payload);
      }
    },
    [queryClient, documentId, deleteTrainingAssessment, saveTrainingAssessment],
  );

  const handleSubmission = useCallback(() => {
    if (!methods.formState.isDirty) return;
    void methods.handleSubmit(async (data) => {
      try {
        await handleSubmit(data);
        const currentValues = methods.getValues();
        const hasChangedDuringUpdate = !isEqual(data, currentValues);
        methods.reset(currentValues);
        if (hasChangedDuringUpdate) {
          debouncedSubmit();
        }
      } catch (e) {
        console.error(e);
      }
    })();
    // RHF returns a new `methods` object reference on every render, but all the
    // methods it contains (handleSubmit, getValues, reset, formState) are stable
    // ref-backed functions. Using [] deps avoids recreating this on every render,
    // which would otherwise cancel in-flight debounces via the watch effect cleanup.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedSubmit = useMemo(
    () => debounce(() => handleSubmission(), 1000),
    [handleSubmission],
  );

  const addQuestionCallback = useCallback(() => {
    append({
      id: undefined,
      stableKey: crypto.randomUUID(),
      question: '',
      answers: [
        {
          stableKey: crypto.randomUUID(),
          answer: '',
          is_correct: true,
        },
        {
          stableKey: crypto.randomUUID(),
          answer: '',
          is_correct: false,
        },
      ],
    });
    debouncedSubmit();
  }, [append, debouncedSubmit]);

  const addSuggestedQuestions = async (
    questions: TrainingAssessmentAIResponse['questions'],
  ) => {
    for (const question of questions) {
      append({
        id: undefined,
        stableKey: crypto.randomUUID(),
        question: question.question,
        answers: question.answers.map((a) => ({
          ...a,
          stableKey: crypto.randomUUID(),
        })),
      });
    }
    await methods.handleSubmit(handleSubmit)();
  };

  // RHF v7 proxy: accessing isDirty here causes RHF to track and update it.
  // Without this, formState.isDirty always returns false inside callbacks.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isDirty: _isDirty } = methods.formState;

  useEffect(() => {
    const subscription = methods.watch(() => {
      debouncedSubmit();
    });
    return () => {
      subscription.unsubscribe();
      debouncedSubmit.cancel();
      setTrainingAssessmentStatus(EditorStatus.STABLE);
      if (methods.formState.isDirty) {
        handleSubmission();
      }
    };
  }, [methods, debouncedSubmit, handleSubmission, setTrainingAssessmentStatus]);

  const formIds = useMemo(() => fields.map((field) => field.id), [fields]);

  const swapCards = useCallback(
    (updatedIds: string[]) => {
      for (const [index, formId] of formIds.entries()) {
        const correctIndex = updatedIds.indexOf(formId);
        if (correctIndex !== index) {
          swap(index, correctIndex);
          break;
        }
      }
    },
    [formIds, swap],
  );

  const deleteCard = useCallback(
    (updatedIds: string[]) => {
      for (const [index, formId] of formIds.entries()) {
        if (!updatedIds.includes(formId)) {
          remove(index);
          break;
        }
      }
    },
    [formIds, remove],
  );

  const onQuestionChange = useCallback(
    (ids: string[]) => {
      if (formIds.length === ids.length) {
        swapCards(ids);
      } else {
        deleteCard(ids);
      }
    },
    [formIds.length, swapCards, deleteCard],
  );

  if (!fields?.length) {
    return (
      <>
        <GenerateTrainingAssessmentAI onAccept={addSuggestedQuestions} />
        <TrainingAssessmentEmptyState onClick={addQuestionCallback} />
      </>
    );
  }

  return (
    <>
      <GenerateTrainingAssessmentAI onAccept={addSuggestedQuestions} />
      <QBox marginTop="16px" data-cy="edit-training-assessment-layout">
        <QBox width="full">
          <FormProvider {...methods}>
            <QReorderableCardStack onChange={onQuestionChange}>
              {fields.map((field, index) => (
                <QReorderableCard id={field.id} key={field.stableKey}>
                  <TrainingAssessmentFormQuestion index={index} />
                </QReorderableCard>
              ))}
              <QButton
                variant="outline"
                leftIcon="Plus"
                onClick={addQuestionCallback}
              >
                {t('buttons.addQuestion')}
              </QButton>
            </QReorderableCardStack>
          </FormProvider>
        </QBox>
      </QBox>
    </>
  );
};
